import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
} from "firebase/functions";
import { firebase } from "./firebase";
import { config } from "@/config";
import { deepFirestoreToDateTime } from "@repo/shared/deepFirestoreDate";

// endpoint wrapper
const http = getFunctions(firebase);

export const ServerError: { new (message: string): Error } =
  function ServerError(message: string) {
    const err = new Error(message);
    Object.setPrototypeOf(err, ServerError.prototype);
    return err;
  } as unknown as { new (message: string): Error };

const makeEndpoint = <RequestData, ResponseData>(name: string) => {
  const call = httpsCallable<
    RequestData,
    { type: "success"; data: ResponseData } | { type: "error"; error: string }
  >(http, name);

  return async (data: RequestData): Promise<ResponseData> => {
    const { data: response } = await call(data);

    if (
      !response ||
      typeof response !== "object" ||
      (response.type !== "success" && response.type !== "error")
    ) {
      throw new Error("Invalid Server Response");
    }

    if (response.type === "error") {
      throw new ServerError(response.error);
    }

    return deepFirestoreToDateTime(response.data);
  };
};

// declare endpoints

const upgrade = makeEndpoint<{ key: string }, { [x: string]: unknown }>(
  "upgrade"
);

const repair = makeEndpoint<{ key: string; tenant?: string }, string[]>(
  "repair"
);

// console-exposed endpoints

const runUpgrade = async function (key: string) {
  return await upgrade({ key });
};

const runRepair = async function (
  key: string = "EMULATION_REPAIR_KEY",
  tenantId?: string
) {
  return await repair({ key, tenant: tenantId });
};

const functions = {
  upgrade,
  repair,
  runUpgrade,
  runRepair,
};

Object.assign(window, {
  runUpgrade,
  runRepair,
});
if (config.emulator) {
  const checkExpiredAgendaItems = makeEndpoint<object, never>(
    "checkExpiredAgendaItems"
  );
  const checkExpiredTeamMembers = makeEndpoint<object, never>(
    "checkExpiredTeamMembers"
  );
  const sendMeetingDateNotifications = makeEndpoint<object, never>(
    "sendMeetingDateNotifications"
  );

  Object.assign(window, {
    checkExpiredAgendaItems,
    checkExpiredTeamMembers,
    sendMeetingDateNotifications,
  });
}

// connect / export
if (config.emulator) {
  connectFunctionsEmulator(http, "localhost", 5001);
}
export { http, functions };
